import React, { useState, useMemo } from 'react'
import {
  Box,
  useMediaQuery,
  useTheme,
  Fade,
  Typography,
  IconButton,
  Stack,
  Container,
  Paper,
  Grid,
} from '@mui/material'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import PushPinIcon from '@mui/icons-material/PushPin'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import ArchiveIcon from '@mui/icons-material/Archive'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import { Sidebar, MobileNav, useArchiveChatPageAPI, ChatSection } from '@components'
import {
  TextField,
  Chip,
  InputAdornment,
  Badge,
  Fab,
  Tooltip,
} from '@mui/material'

interface Chat {
  chat_uuid: string
  char_uuid: string
  name: string
  description: string
  profile_image: string
  messageCount: number
  is_pinned: boolean
}

export function Archive() {
  const [open, setOpen] = useState(true)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [itemsToShow, setItemsToShow] = useState(100)
  const [searchTerm, setSearchTerm] = useState('')
  const [archivedView, setArchivedView] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const handleClose = () => setOpen(false)

  const {
    chats,
    isLoading,
    handleCardClick,
    handleDeleteChat,
    handlePinChat,
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    setChatType,
  } = useArchiveChatPageAPI(open, handleClose, itemsToShow)

  const uniqueChats = useMemo(() => {
    const seen = new Set()
    const result: Chat[] = []
    
    chats.forEach((chat: Chat) => {
      if (chat.is_pinned && !seen.has(chat.char_uuid)) {
        seen.add(chat.char_uuid)
        result.push(chat)
      }
    })
    
    chats.forEach((chat: Chat) => {
      if (!chat.is_pinned && !seen.has(chat.char_uuid)) {
        seen.add(chat.char_uuid)
        result.push(chat)
      }
    })
    
    return result
  }, [chats])

  const displayChats = useMemo(() => {
    if (!searchTerm.trim()) return uniqueChats
    return uniqueChats.filter((chat: Chat) =>
      chat.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
    )
  }, [uniqueChats, searchTerm])

  const { pinnedChats, unpinnedChats } = useMemo(() => {
    if (archivedView) {
      return { pinnedChats: [], unpinnedChats: displayChats }
    }
    const pinned = displayChats.filter((c: Chat) => c.is_pinned)
    const unpinned = displayChats.filter((c: Chat) => !c.is_pinned)
    return { pinnedChats: pinned, unpinnedChats: unpinned }
  }, [displayChats, archivedView])

  const toggleArchived = () => {
    const newArchived = !archivedView
    setArchivedView(newArchived)
    setChatType(newArchived ? 'archived' : 'normal')
    setSearchTerm('')
  }

  const clearSearch = () => setSearchTerm('')

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
        display: 'flex',
      }}
    >
      <Sidebar />
      {isMobile && <MobileNav setShowLoginModal={setShowLoginModal} />}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { md: '72px' },
          minHeight: '100vh',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: 4,
            px: { xs: 2, md: 4 },
          }}
        >
          <Fade in timeout={600}>
            <Box>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 600,
                  mx: 'auto',
                  p: 4,
                  mb: 4,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} mb={3}>
                  <Badge badgeContent={displayChats.length} sx={{ '& .MuiBadge-badge': { backgroundColor: '#667eea', color: '#fff' } }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 32, color: '#667eea' }} />
                  </Badge>
                  <Typography variant="h4" fontWeight={600} color="#fff">
                    {archivedView ? 'Archive' : 'Chats'}
                  </Typography>
                </Stack>

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search characters..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearSearch} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    maxWidth: 500,
                    mx: 'auto',
                    display: 'block',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: 'rgba(255,255,255,0.4)',
                    },
                  }}
                />

                <Stack direction="row" spacing={1} justifyContent="center" mt={3}>
                  {!archivedView && (
                    <Chip
                      icon={<PushPinIcon sx={{ color: pinnedChats.length > 0 ? '#fff !important' : 'rgba(255,255,255,0.6)' }} />}
                      label={`${pinnedChats.length} Pinned`}
                      sx={{
                        backgroundColor: pinnedChats.length > 0 ? '#667eea' : 'rgba(255,255,255,0.08)',
                        color: pinnedChats.length > 0 ? '#fff' : 'rgba(255,255,255,0.6)',
                        '& .MuiChip-label': { color: pinnedChats.length > 0 ? '#fff' : 'rgba(255,255,255,0.6)' },
                      }}
                      size="small"
                    />
                  )}
                  <Chip
                    icon={<ChatBubbleOutlineIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />}
                    label={`${unpinnedChats.length} ${archivedView ? 'Archived' : 'Active'}`}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      color: 'rgba(255,255,255,0.6)',
                      '& .MuiChip-label': { color: 'rgba(255,255,255,0.6)' },
                    }}
                    size="small"
                  />
                </Stack>
              </Paper>

              <Box sx={{ width: '100%' }}>
                {isLoading ? (
                  <ChatSection
                    pinnedChats={[]}
                    unpinnedChats={[]}
                    isLoading={true}
                    onPin={handlePinChat}
                    onDelete={handleDeleteChat}
                    onClick={handleCardClick}
                  />
                ) : displayChats.length === 0 ? (
                  <Fade in>
                    <Paper
                      elevation={0}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 10,
                        px: 3,
                        textAlign: 'center',
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
                        {searchTerm
                          ? `No matches for "${searchTerm}"`
                          : archivedView
                          ? 'Archive is empty'
                          : 'No conversations yet'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                        {searchTerm
                          ? 'Try a different search term'
                          : archivedView
                          ? 'Archive chats to see them here'
                          : 'Start chatting to see conversations here'}
                      </Typography>
                    </Paper>
                  </Fade>
                ) : (
                  <Fade in>
                    <Box>
                      <ChatSection
                        pinnedChats={archivedView ? [] : pinnedChats}
                        unpinnedChats={unpinnedChats}
                        isLoading={false}
                        onPin={handlePinChat}
                        onDelete={handleDeleteChat}
                        onClick={handleCardClick}
                      />

                      {!searchTerm && (hasNextPage || hasPreviousPage) && (
                        <Paper
                          elevation={0}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mt: 4,
                            gap: 2,
                            p: 2,
                            borderRadius: 3,
                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            maxWidth: 300,
                            mx: 'auto',
                          }}
                        >
                          <IconButton
                            onClick={goToPreviousPage}
                            disabled={!hasPreviousPage}
                            size="small"
                            sx={{
                              color: !hasPreviousPage ? 'rgba(255,255,255,0.3)' : '#667eea',
                              transition: 'transform 0.2s',
                              '&:hover:not(:disabled)': {
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            <NavigateBeforeIcon />
                          </IconButton>

                          <IconButton
                            onClick={goToNextPage}
                            disabled={!hasNextPage}
                            size="small"
                            sx={{
                              color: !hasNextPage ? 'rgba(255,255,255,0.3)' : '#667eea',
                              transition: 'transform 0.2s',
                              '&:hover:not(:disabled)': {
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            <NavigateNextIcon />
                          </IconButton>
                        </Paper>
                      )}
                    </Box>
                  </Fade>
                )}
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Tooltip title={archivedView ? 'Active Chats' : 'Archived'} placement="left">
        <Fab
          onClick={toggleArchived}
          sx={{
            position: 'fixed',
            right: { xs: 16, md: 24, lg: 32 },
            bottom: isMobile ? 80 : 24,
            backgroundColor: '#667eea',
            color: '#fff',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              backgroundColor: '#5a6fd6',
              transform: 'scale(1.05)',
              boxShadow: '0 8px 25px -5px rgba(102, 126, 234, 0.4)',
            },
          }}
        >
          {archivedView ? <UnarchiveIcon /> : <ArchiveIcon />}
        </Fab>
      </Tooltip>
    </Box>
  )
}